import { createContext, FC, useContext, useState } from "react";

type WorkerProps = {
  workerName: string;
  workerUrl: string;
  data: any;
  successMessage: string;
  pendingMessage: string;
  errorMessage: string;
  onSuccess: (data: any) => void;
  onError: () => void;
};

interface WorkerContextProps {
  isWorking: boolean;
  setIsWorking: (isWorking: boolean) => void;
  onPostMessage: (data: any, workerProps: WorkerProps) => Promise<void>;
  setWorker: (worker: Worker | null) => Promise<boolean>;
}

const initialWorker: WorkerContextProps = {
  isWorking: false,
  setIsWorking: () => {},
  onPostMessage: async () => Promise.resolve(),
  setWorker: async () => Promise.resolve(false),
};

export const WorkerContext = createContext<WorkerContextProps>(initialWorker);

export const WorkerProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  let currentWorker: Worker | null = null;
  const [isWorking, setIsWorking] = useState(false);

  const postMessageWithPromise = (data: any, workerProps: WorkerProps) => {
    return new Promise((resolve, reject) => {
      if (!currentWorker) {
        reject(new Error("Worker is not initialized."));
        return;
      }

      const messageHandler = (event: MessageEvent) => {
        currentWorker!.removeEventListener("message", messageHandler);
        if (workerProps.onSuccess) {
          workerProps.onSuccess(event.data);
        }
        setIsWorking(false);
        resolve(event.data);
        currentWorker!.terminate();
        currentWorker = null;
      };

      currentWorker.addEventListener("message", messageHandler);
      currentWorker.postMessage(data);
      setIsWorking(true);
    });
  };

  const onPostMessage = async (
    data: any,
    workerProps: WorkerProps
  ): Promise<void> => {
    await postMessageWithPromise(data, workerProps);
  };

  const setWorker = (worker: Worker | null): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        currentWorker = worker;
        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  };

  return (
    <WorkerContext.Provider
      value={{ isWorking, setIsWorking, onPostMessage, setWorker }}
    >
      {children}
    </WorkerContext.Provider>
  );
};

export const useWorker = () => useContext(WorkerContext);
